import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Card } from '@/shared/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Textarea } from '@/shared/components/ui/textarea'
import { cn } from '@/shared/lib/utils'
import {
    Camera,
    Check,
    Clock,
    Film,
    Image as ImageIcon,
    Maximize2,
    MessageSquare,
    Pencil,
    Sparkles,
    Trash2,
    Wand2,
    X
} from 'lucide-react'
import React, { useState } from 'react'
import { StoryboardFrame as StoryboardFrameType } from '../types/storyboard.types'
import ReferenceImagePicker from './ReferenceImagePicker'

interface StoryboardFrameProps {
    frame: StoryboardFrameType
    /** Override the displayed image URL (e.g. with a cache-buster after regeneration). Falls back to frame.imageUrl. */
    displayImageUrl?: string
    onViewImage?: (imageUrl: string) => void
    /** Re-generate just the image using the current visual description */
    onRegenerateImage?: () => void
    isRegeneratingImage?: boolean
    /** Re-generate the entire frame (description + image) with AI */
    onRegenerateFrame: () => void
    isRegeneratingFrame?: boolean
    onUpdate: (frameId: string, updates: Partial<StoryboardFrameType>) => void
    onRemove: (frameId: string) => void
    defaultEditing?: boolean
}

const StoryboardFrame: React.FC<StoryboardFrameProps> = ({
    frame,
    displayImageUrl,
    onViewImage,
    onRegenerateImage,
    isRegeneratingImage,
    onRegenerateFrame,
    isRegeneratingFrame,
    onUpdate,
    onRemove,
    defaultEditing = false
}) => {
    const imageSrc = displayImageUrl ?? frame.imageUrl
    // const { updateFrame, removeFrame } = useStoryboardStore() // Removed
    const [isEditing, setIsEditing] = useState(defaultEditing)
    const [formData, setFormData] = useState({
        shotDescription: frame.shotDescription,
        visualDescription: frame.visualDescription,
        duration: frame.duration || '',
        cameraAngle: frame.cameraAngle || '',
        cameraMovement: frame.cameraMovement || '',
        dialogueNotes: frame.dialogueNotes || '',
        referenceImageUrl: frame.referenceImageUrl || ''
    })

    const handleSave = () => {
        onUpdate(frame.id, formData)
        setIsEditing(false)
    }

    const handleDelete = () => {
        if (window.confirm("Are you sure you want to delete this frame?")) {
            onRemove(frame.id)
        }
    }

    if (isEditing) {
        return (
            <Card className="overflow-hidden border-primary/50 shadow-lg relative p-4 bg-muted/20">
                <div className="flex items-center justify-between border-b pb-2 mb-4">
                    <Badge variant="secondary" className="font-mono">
                        Frame #{frame.frameNumber} (Editing)
                    </Badge>
                    <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)}>
                            <X className="h-4 w-4 mr-1" />
                            Cancel
                        </Button>
                        <Button size="sm" onClick={handleSave}>
                            <Check className="h-4 w-4 mr-1" />
                            Save Changes
                        </Button>
                    </div>
                </div>

                <div className="flex gap-4 flex-col md:flex-row">
                    {/* Left: Image + Reference */}
                    <div className="w-full md:w-1/3 space-y-3">
                        {/* Current generated image – read-only thumbnail */}
                        {frame.imageUrl && (
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Current Image</label>
                                <div className="aspect-video rounded-lg overflow-hidden border border-border bg-muted/20">
                                    <img src={frame.imageUrl} alt={frame.shotDescription} className="w-full h-full object-cover" />
                                </div>
                            </div>
                        )}

                        {/* Reference image picker */}
                        <ReferenceImagePicker
                            value={formData.referenceImageUrl || undefined}
                            onChange={(url) => setFormData(prev => ({ ...prev, referenceImageUrl: url ?? '' }))}
                        />

                        <div className="space-y-1">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Visual Description (AI Prompt)</label>
                            <Textarea
                                value={formData.visualDescription}
                                onChange={(e) => setFormData(prev => ({ ...prev, visualDescription: e.target.value }))}
                                className="min-h-[80px] text-sm"
                                placeholder="Visual details for image generation..."
                            />
                        </div>
                    </div>

                    {/* Right: Details Editing */}
                    <div className="flex-1 space-y-4">
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Shot Action</label>
                            <Textarea
                                value={formData.shotDescription}
                                onChange={(e) => setFormData(prev => ({ ...prev, shotDescription: e.target.value }))}
                                className="min-h-[60px]"
                                placeholder="Describe the action..."
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Duration</label>
                                <Input
                                    value={formData.duration}
                                    onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
                                    placeholder="e.g. 2s"
                                    className="h-8 text-xs"
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Camera Angle</label>
                                <Input
                                    value={formData.cameraAngle}
                                    onChange={(e) => setFormData(prev => ({ ...prev, cameraAngle: e.target.value }))}
                                    placeholder="e.g. Wide"
                                    className="h-8 text-xs"
                                />
                            </div>
                            <div className="space-y-1 col-span-2">
                                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Camera Movement</label>
                                <Input
                                    value={formData.cameraMovement}
                                    onChange={(e) => setFormData(prev => ({ ...prev, cameraMovement: e.target.value }))}
                                    placeholder="e.g. Pan Right"
                                    className="h-8 text-xs"
                                />
                            </div>
                        </div>

                        <div className="space-y-1">
                            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Dialogue Notes</label>
                            <Textarea
                                value={formData.dialogueNotes}
                                onChange={(e) => setFormData(prev => ({ ...prev, dialogueNotes: e.target.value }))}
                                className="min-h-[40px] text-sm"
                                placeholder="Any dialogue associated with this frame..."
                            />
                        </div>
                    </div>
                </div>
            </Card>
        )
    }

    return (
        <Card className="overflow-hidden hover:shadow-lg transition-all group border-border/50 hover:border-primary/30 relative">
            <div className="flex flex-col md:flex-row h-full w-full">
                {/* Left: Image Section (Take up ~35-40%) */}
                <div className="w-full md:w-[320px] lg:w-[400px] bg-black/5 relative flex-shrink-0 group/image">
                    {imageSrc ? (
                        <div className="relative w-full h-full min-h-[225px]">
                            <img
                                src={imageSrc}
                                alt={frame.shotDescription}
                                className="w-full h-full object-cover absolute inset-0"
                            />
                            {/* Expand button: top-right corner */}
                            {onViewImage && (
                                <button
                                    onClick={() => onViewImage?.(imageSrc!)}
                                    title="Expand image"
                                    className="absolute top-2 right-2 z-10 bg-black/50 hover:bg-black/80 text-white rounded-md p-1.5 opacity-0 group-hover/image:opacity-100 transition-opacity"
                                >
                                    <Maximize2 className="h-3.5 w-3.5" />
                                </button>
                            )}

                            {/* Reference Image Preview: Creative Mood Scrap aesthetic (Bottom Placement) */}
                            {frame.referenceImageUrl && (
                                <div className="absolute bottom-10 left-3 z-10 group/ref">
                                    <div className="relative h-16 w-16 sm:h-20 sm:w-20 bg-white p-1 shadow-2xl rounded-sm rotate-3 group-hover/ref:rotate-0 group-hover/ref:scale-110 transition-all duration-300 ring-1 ring-black/5">
                                        {/* Physical Tape Effect - side tape for bottom placement */}
                                        <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-3 h-8 bg-white/40 backdrop-blur-sm border border-white/20 -rotate-12 z-20 shadow-sm" />

                                        <div className="relative h-full w-full overflow-hidden bg-muted">
                                            <img
                                                src={frame.referenceImageUrl}
                                                alt="Reference Mood"
                                                className="h-full w-full object-cover"
                                                referrerPolicy="no-referrer"
                                            />
                                            <div className="absolute inset-0 bg-black/10 group-hover/ref:bg-black/0 transition-colors" />
                                        </div>

                                        {/* Label Sticker */}
                                        <div className="absolute -top-1 -right-2 bg-primary px-1.5 py-0.5 rounded-sm shadow-sm -rotate-3 z-30">
                                            <p className="text-[8px] text-white font-bold uppercase tracking-widest leading-none">Mood</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {/* Regenerate Image: bottom bar */}
                            {onRegenerateImage && (
                                <div className="absolute bottom-0 inset-x-0 opacity-0 group-hover/image:opacity-100 transition-opacity">
                                    <button
                                        onClick={onRegenerateImage}
                                        disabled={isRegeneratingImage}
                                        className="w-full flex items-center justify-center gap-1.5 py-4 bg-black/60 hover:bg-black/80 text-white text-xs font-medium transition-colors disabled:opacity-60"
                                    >
                                        <Wand2 className={cn("h-3.5 w-3.5", isRegeneratingImage && "animate-spin")} />
                                        {isRegeneratingImage ? "Regenerating..." : "Regenerate Image"}
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="w-full h-full min-h-[225px] flex flex-col items-center justify-center bg-muted/30 p-6 text-center gap-3">
                            <ImageIcon className="h-10 w-10 text-muted-foreground/40" />
                            <p className="text-xs text-muted-foreground line-clamp-3 italic max-w-[200px]">
                                "{frame.visualDescription}"
                            </p>
                            {onRegenerateImage && (
                                <button
                                    onClick={onRegenerateImage}
                                    disabled={isRegeneratingImage}
                                    className="mt-1 flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary/10 hover:bg-primary/20 text-primary text-xs font-medium transition-colors disabled:opacity-60"
                                >
                                    <Wand2 className={cn("h-3.5 w-3.5", isRegeneratingImage && "animate-spin")} />
                                    {isRegeneratingImage ? "Generating..." : "Generate Image"}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Number Badge removed from image corner in favor of header numbering */}
                </div>

                {/* Right: Content & Actions */}
                <div className="flex-1 p-5 flex flex-col justify-between w-full min-w-0">
                    <div className="space-y-4">
                        {/* Header: Shot Description & Duration */}
                        <div className="flex justify-between items-start gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-mono text-muted-foreground font-bold">#{frame.frameNumber}</span>
                                <h3 className="font-semibold text-lg leading-tight line-clamp-2" title={frame.shotDescription}>
                                    {frame.shotDescription || <span className="text-muted-foreground italic">No Description</span>}
                                </h3>
                            </div>
                            {frame.duration && (
                                <Badge variant="outline" className="flex-shrink-0 gap-1.5 h-7">
                                    <Clock className="w-3.5 h-3.5" />
                                    {frame.duration}
                                </Badge>
                            )}
                        </div>

                        <p className="text-sm text-muted-foreground leading-relaxed min-h-[1.25rem]">
                            {/* Frame doesn't have a separate description vs subject, using visual description as subtext if needed, but dialogue covers it */}
                            <span className="italic line-clamp-3">"{frame.visualDescription}"</span>
                        </p>

                        {/* Camera & Tech Specs */}
                        <div className="flex flex-wrap gap-2 text-sm text-muted-foreground">
                            {frame.cameraAngle && (
                                <div className="flex items-center gap-1.5 bg-muted/50 px-2 py-1 rounded-md text-xs">
                                    <Camera className="w-3.5 h-3.5" />
                                    <span className="font-medium">Angle:</span> {frame.cameraAngle}
                                </div>
                            )}
                            {frame.cameraMovement && (
                                <div className="flex items-center gap-1.5 bg-muted/50 px-2 py-1 rounded-md text-xs">
                                    <Film className="w-3.5 h-3.5" />
                                    <span className="font-medium">Move:</span> {frame.cameraMovement}
                                </div>
                            )}
                        </div>

                        {/* Dialogue Preview */}
                        {frame.dialogueNotes && (
                            <div className="text-xs text-muted-foreground italic border-t border-border/40 pt-2 mt-2">
                                <span className="flex items-start gap-1">
                                    <MessageSquare className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                                    "{frame.dialogueNotes}"
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Footer Actions */}
                    <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t border-border/40">

                        {/* Regenerate full frame with AI */}
                        {onRegenerateFrame && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={onRegenerateFrame}
                                disabled={isRegeneratingFrame}
                                className=""
                            >
                                <Sparkles className={cn("h-4 w-4 mr-2 transition-transform duration-300", isRegeneratingFrame && "animate-pulse")} />
                                {isRegeneratingFrame ? "Regenerating..." : "Regenerate Frame"}
                            </Button>
                        )}  {/* Edit Text */}
                        <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit Text
                        </Button>


                        {/* Delete */}
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            onClick={handleDelete}
                            title="Remove Frame"
                        >
                            <Trash2 className="h-4 w-4" />
                            <span className="sr-only">Delete</span>
                        </Button>
                    </div>
                </div>
            </div>
        </Card>
    )
}

export default StoryboardFrame
